import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/scripts/AuthContext";
import { listTeamChannels, sendDmMessage } from "@/lib/dmApi";
import { postAttachment } from "@/lib/permalinks";
import { markdownToPlain } from "@/lib/markdownPlain";
import { toast, toastError } from "@/lib/toast";

function conversationIdOf(channel) {
  return channel?.conversation_id ?? channel?.conversationId ?? null;
}

function sortTeamChannels(list) {
  return [...(list || [])].sort((a, b) => {
    if (a.is_default && !b.is_default) return -1;
    if (!a.is_default && b.is_default) return 1;
    return String(a.title || "").localeCompare(String(b.title || ""));
  });
}

/** Same posting-policy hint DmThread uses when the user cannot send. */
export function teamChannelDiscussHint(channel) {
  if (!channel) return "Team channel isn’t loaded yet.";
  const conversationId = conversationIdOf(channel);
  if (channel.is_member === false) return "Join the team channel to discuss.";
  if (channel.can_send === false) {
    return channel.posting_policy === "custom"
      ? "You can’t post in this channel."
      : "Only team members can post in this channel.";
  }
  if (!conversationId) return "Team channel isn’t available.";
  return null;
}

/**
 * Share a card/post permalink into a channel on this team.
 */
export default function ShareToTeamChannelDialog({
  open,
  onOpenChange,
  orgId,
  teamId,
  post,
  channel,
  onViewChannel,
}) {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const postId = post?.post_id ?? post?.postId;
  const title = markdownToPlain(post?.title || "").trim();
  const isCard = !!(post?.kanbanStatus || post?.kanban_status);

  const selected = useMemo(() => {
    const id = selectedId != null ? String(selectedId) : "";
    return (
      channels.find((ch) => String(conversationIdOf(ch)) === id) ||
      null
    );
  }, [channels, selectedId]);

  const conversationId = conversationIdOf(selected);
  const disabledReason = loading
    ? "Loading channels…"
    : channels.length === 0
      ? "No team channels yet."
      : teamChannelDiscussHint(selected);
  const canSend = !disabledReason;

  useEffect(() => {
    if (!open) {
      setNote("");
      setSending(false);
      return;
    }
    if (!accessToken || !orgId || !teamId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = sortTeamChannels(
          await listTeamChannels(accessToken, orgId, teamId)
        );
        if (cancelled) return;
        setChannels(list);
        const preferred =
          conversationIdOf(channel) ||
          conversationIdOf(list.find((ch) => ch.is_default)) ||
          conversationIdOf(list[0]);
        setSelectedId(preferred != null ? String(preferred) : null);
      } catch (err) {
        if (!cancelled) {
          setChannels([]);
          setSelectedId(null);
          toastError(err, "Could not load team channels");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, accessToken, orgId, teamId, channel]);

  const handleOpenChange = (next) => {
    if (!next) setNote("");
    onOpenChange?.(next);
  };

  const handleView = () => {
    if (selected?.is_default && typeof onViewChannel === "function") {
      onViewChannel();
      return;
    }
    if (orgId && conversationId) {
      navigate(
        `/o/${encodeURIComponent(orgId)}/messages/${conversationId}`,
        { state: { channel: selected } }
      );
    }
  };

  const handleSend = async () => {
    if (!canSend || !accessToken || !orgId || postId == null || sending) return;
    setSending(true);
    try {
      await sendDmMessage(accessToken, orgId, conversationId, {
        content: note.trim(),
        attachment: postAttachment({
          orgId,
          postId,
          title: title || undefined,
          kind: isCard ? "card" : "post",
        }),
      });
      toast.success("Shared to team channel", {
        action: {
          label: "View",
          onClick: handleView,
        },
      });
      setNote("");
      onOpenChange?.(false);
    } catch (err) {
      toastError(err, "Could not share to team channel");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Discuss in a team channel</DialogTitle>
          <DialogDescription>
            Share a permalink into a channel on this team.
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-foreground line-clamp-2 rounded-md border bg-muted/30 px-3 py-2">
          {title || `${isCard ? "Card" : "Post"} #${postId ?? ""}`}
        </p>
        <div className="space-y-1.5">
          <Label htmlFor="discuss-channel" className="text-xs">
            Channel
          </Label>
          <div className="flex items-center gap-2">
            <Hash className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <select
              id="discuss-channel"
              className="h-9 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={selectedId || ""}
              disabled={loading || sending || channels.length === 0}
              onChange={(e) => setSelectedId(e.target.value || null)}
            >
              {channels.length === 0 ? (
                <option value="">
                  {loading ? "Loading…" : "No channels"}
                </option>
              ) : (
                channels.map((ch) => {
                  const id = conversationIdOf(ch);
                  return (
                    <option key={id} value={String(id)}>
                      {ch.title || "Channel"}
                      {ch.is_default ? " (default)" : ""}
                    </option>
                  );
                })
              )}
            </select>
          </div>
        </div>
        {disabledReason ? (
          <p className="text-sm text-muted-foreground">{disabledReason}</p>
        ) : (
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note"
            disabled={sending}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canSend || sending || postId == null}
            onClick={handleSend}
          >
            {sending ? "Sending…" : "Share"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
